import { Banner } from '@/components/banner/banner.comp';
import { CategoriesCatalog } from '@/components/categoriesCatalog/categoriesCatalog.comp';
import { Layout } from '@/components/layout/layout.comp';
import { PlatformOverview } from '@/components/platformOverview/platformOverview.comp';
import { SearchHero } from '@/components/searchHero/searchHero.comp';
import { WidgetCatalog } from '@/components/widgetCatalog/widgetCatalog.comp';
import { getDataFromDB } from '@/lib/mongodb';
import { countApps } from '@/lib/search';
import { getSearchEngineDataDB } from '@/lib/searchEngineMongodb';
import { IPlatform, IPlugin } from '@/types';
import { GetStaticPropsContext } from 'next';
import Script from 'next/script';

export default function PlatformPage({
	apps,
	platform,
	categories,
	totalApps,
}: {
	apps: IPlugin[];
	platform: IPlatform;
	categories: any[];
	totalApps: number;
}) {
	return (
		<Layout
			showFooter={true}
			showHeader={true}
			title={`Best ${platform.appSynonym}s for ${
				platform.displayName
			} in ${new Date().getFullYear()} | Common Ninja`}
			description={`Discover the top ${platform.appSynonym}s for ${platform.displayName}.`}
		>
			{loadScripts(platform)}
			<SearchHero platform={platform} total={totalApps} />
			<WidgetCatalog platform={platform} apps={apps} />
			<CategoriesCatalog categories={categories} platform={platform} />
			<PlatformOverview platform={platform} />
			<Banner />
		</Layout>
	);
}

export async function getStaticPaths() {
	try {
		const platforms = await getSearchEngineDataDB<any>('platforms');
		const paths = platforms.map((platform) => ({
			params: {
				platform: platform.name,
			},
		}));

		return { paths, fallback: false };
	} catch (e) {
		console.error(e);
		return { paths: [], fallback: false };
	}
}

export async function getStaticProps({
	params = { platform: '' },
}: GetStaticPropsContext<{ platform: string }>) {
	const wantedPlatform = await getSearchEngineDataDB<IPlatform>('platforms', {
		name: params.platform,
	});
	const categories = await getSearchEngineDataDB(
		'seocategories',
		{
			platform: params.platform,
		},
		{
			displayName: 1,
			slug: 1,
		}
	);

	const apps = await getDataFromDB<IPlugin>('apps', {
		status: 'published',
	});
	const randomApps = apps.sort(() => 0.5 - Math.random()).splice(0, 12);
	const totalApps = await countApps(params.platform);
	return {
		props: {
			apps: randomApps,
			platform: wantedPlatform[0],
			categories,
			totalApps,
		},
	};
}

function loadScripts(platform: IPlatform) {
	return (
		<>
			<Script id="platform-script-1" type="application/ld+json">{`
         {
          "@context": "https://schema.org/",
          "@type": "WebSite",
          "name": "Find the best ${platform.appSynonym}s for ${platform.displayName} | Common Ninja",
          "url": "https://discover.commoninja.com/${platform.name}/",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://discover.commoninja.com/${platform.name}/results?q={search_term_string}&orderBy=popular",
            "query-input": "required name=search_term_string"
          }
        }
        `}</Script>
			<Script id="platform-script-2" type="application/ld+json">{`
            {
          "@context": "https://schema.org/",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Discover",
            "item": "https://discover.commoninja.com/"
          },{
            "@type": "ListItem",
            "position": 2,
            "name": "${platform.displayName}",
            "item": "https://discover.commoninja.com/${platform.name}"  
          }]
        }
        `}</Script>
		</>
	);
}
